import { formValueSelector } from 'redux-form';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { hideLoader, showLoader } from '@topconagriculture/loader';

import { RationTypeCode } from '../../../config/constants';
import api from '../../../core/api';
import { formatDate } from '../../../core/helpers/utils/dateUtils';
import { RecipeTransitions } from '../../../routes/ManageProducts/Recipes/shared/RecipeLeftSidebar/RecipeTransitionField/RecipeTransitionField';
import { Ingredient } from '../../entities/ingredients/ingredients.types';
import { IngredientInRation } from '../../entities/premixes/premixes.types';
import { getRecipe } from '../../entities/recipes/recipes.actions';
import { Recipe } from '../../entities/recipes/recipes.types';
import { Forms } from '../../form/form.selector';
import { appRecipeSelector, ingredientsSelector, recipesSelector, selectedSelector } from '../../selectors';
import { LoadersNames } from '../../ui/ui.constants';
import { changeIngredient, previewCreateRecipe, sendRecipePayload, updateBaseRationRecipe } from './appRecipes.actions';
import { CreateRationPayload, TargetWeights } from './appRecipes.types';

export function* previewCreateRecipeSaga(action: any) {
  yield put(showLoader(LoadersNames.previewRecipe));
  const {
    description,
    displayName,
    density,
    adjOnDM,
    notInUse,
    autoAdjWater,
    targetDMPerc,
    recipeType,
    finalDateBeforeTransition,
    recipeTransition,
  } = yield select(
    formValueSelector(Forms.recipeForm),
    'description',
    'displayName',
    'density',
    'adjOnDM',
    'notInUse',
    'autoAdjWater',
    'targetDMPerc',
    'recipeType',
    'finalDateBeforeTransition',
    'recipeTransition',
  );
  const targetWeightType = yield select(formValueSelector(Forms.recipeIngredientsForm), 'targetWeightType');

  const recipePayload = {
    ...(yield select(appRecipeSelector.getRecipePayload)),
  };

  try {
    if (recipeType === RationTypeCode.FirstIngredientResize || recipeType === RationTypeCode.RefusalSubstitution) {
      recipePayload.ingredients = recipePayload.ingredients.filter(
        (ingredient) => ingredient.ingredientId !== -1 && ingredient.amounts !== 0,
      );
    }

    const ration: CreateRationPayload = {
      displayName: displayName || ' ',
      description: description || ' ',
      density: density || 100,
      adjOnDM,
      notInUse,
      autoAdjWater,
      percEntryMethod: targetWeightType === TargetWeights.DRY ? 1 : 2,
      targetDMPerc: autoAdjWater ? Number(targetDMPerc) : 0,
      rationTypeCode: recipeType,
      finalDateBeforeTransition:
        typeof finalDateBeforeTransition !== 'undefined' && recipeTransition === RecipeTransitions.FIXED_DATES
          ? formatDate(finalDateBeforeTransition, 'yyyy-MM-dd')
          : undefined,
      ...recipePayload,
    };

    const isEditMode = yield select(appRecipeSelector.getIsEditMode);
    const result = isEditMode
      ? yield call(api.recipes.simulateEditRecipe, yield select(selectedSelector.getSelectedRecipeId), ration)
      : yield call(api.recipes.simulateCreateRecipe, ration);

    yield put(previewCreateRecipe.done({ params: {}, result }));
  } catch (err) {
    if (err.status === 409) {
      yield put(previewCreateRecipe.done({ params: {}, result: err.data }));
    } else {
      yield put(previewCreateRecipe.failed({ params: {}, error: {} }));
    }
  }
  yield put(hideLoader(LoadersNames.previewRecipe));
}

export function* updateBaseRationRecipeSaga(action: ReturnType<typeof updateBaseRationRecipe.started>) {
  yield put(showLoader(LoadersNames.fetchRecipeInBaseRation));
  const { recipeId } = action.payload;
  try {
    if (recipeId !== String(-1) && recipeId) {
      const recipe: Recipe = (yield select(recipesSelector.getRecipeById))(recipeId);
      const resultRecipe = recipe.ingredients ? recipe : yield call(api.recipes.getRecipe, recipeId);
      const ingredients = yield all(
        resultRecipe.ingredients.map(function* (ing: IngredientInRation) {
          const ingredient: Ingredient = yield select(ingredientsSelector.getIngredientByIdNormal, ing.ingredientId);
          return {
            ...ing,
            dryMatterPerc: ingredient.dryMatterPerc,
          };
        }),
      );
      const payload = {
        params: action.payload,
        result: {
          ...resultRecipe,
          ingredients,
        },
      };
      yield put(updateBaseRationRecipe.done(payload));
      yield put(getRecipe.done(payload));
    }
  } catch (err) {
    yield put(updateBaseRationRecipe.failed({ params: action.payload, error: err }));
  }
  yield put(hideLoader(LoadersNames.fetchRecipeInBaseRation));
}

export function* changeIngredientSaga(action: ReturnType<typeof changeIngredient.started>) {
  const changedIng = action.payload;
  const oldIng = yield select(appRecipeSelector.getIngredientInRecipeByUUID, changedIng.uuid);

  if (oldIng) {
    yield put(
      changeIngredient.done({
        params: changedIng,
        result: changedIng,
      }),
    );
  }
}

export function* watchAppRecipesSaga() {
  yield all([
    yield takeLatest(updateBaseRationRecipe.started, updateBaseRationRecipeSaga),
    yield takeLatest(sendRecipePayload.type, previewCreateRecipeSaga),
    yield takeLatest(changeIngredient.started, changeIngredientSaga),
  ]);
}
